// Where this build of Focus runs: Loom's web app (the default, and the only
// host the web entry ever has) or the Focus panel, an MCP App inside an
// assistant's conversation (`src/panel/`, the second Vite entry).
//
// The panel's entry calls `installPanelHost` once, before React renders. The
// web entry never does, so every function here keeps the web's behaviour
// exactly: `window.open`, `location.assign`, and a boot gate that is always
// open, and a reload that reloads the page. Focus modules read this seam
// instead of knowing about the panel.
//
// Module state, not a React context: it is fixed before the first render and
// read by non-component code too (`lib/sessionBeacon.ts`).

import { useSyncExternalStore } from "react";

export interface PanelHost {
  /** Open an outside page (an issue on GitLab, a merge request) through the host's link box. */
  readonly openExternal: (url: string) => void;
  /** Open a page of Loom's web app (a path on Loom's origin) in the user's browser. */
  readonly openLoomPage: (path: string) => void;
  /** The sign-in behind the panel is gone: show the panel's signed-out notice. */
  readonly signedOut: () => void;
  /**
   * Start Focus again where it is, without reloading the document: a host
   * may not answer a reloaded MCP App's `ui/initialize` a second time.
   */
  readonly reload: () => void;
}

/**
 * The DOM event the panel dispatches on `window` when its host tears it down
 * (`ui/resource-teardown`). Whatever detaches on `pagehide` listens for it
 * too (`lib/sessionBeacon.ts`); the web never fires it.
 */
export const PANEL_TEARDOWN_EVENT = "focus-panel-teardown";

let panel: PanelHost | null = null;

/** The panel's entry, once, before render. */
export const installPanelHost = (host: PanelHost): void => {
  panel = host;
};

/** True inside the Focus panel; false in the web app. */
export const isPanelHost = (): boolean => panel !== null;

/** The web: a new tab. The panel: the host's link box. */
export const openExternal = (url: string): void => {
  if (panel !== null) {
    panel.openExternal(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

/** The web: a full navigation to `path`. The panel: that page in the browser. */
export const openLoomPage = (path: string): void => {
  if (panel !== null) {
    panel.openLoomPage(path);
    return;
  }
  window.location.assign(path);
};

/** The web: the sign-in page. The panel: its signed-out notice (signing in happens in the assistant). */
export const signInAgain = (): void => {
  if (panel !== null) {
    panel.signedOut();
    return;
  }
  window.location.assign("/sign-in");
};

/**
 * The web: reload the page. The panel: Focus starts again at the screen it is
 * on (a fresh mount, as a reload gives the web), in the same document.
 */
export const reloadApp = (): void => {
  if (panel !== null) {
    panel.reload();
    return;
  }
  window.location.reload();
};

// ---------------------------------------------------------------------------
// The boot gate. The web boots a sandbox when a stage needs one (the
// pre-warm). The panel may be mounted with nobody looking at it (an old
// conversation scrolled into view, a re-mount after a reload), so there it
// boots nothing until the first trusted gesture inside the panel
// (`src/panel/boot-gate.ts`). On the web the gate is open from the start and
// never closes.
// ---------------------------------------------------------------------------

let bootAllowed = true;
const bootListeners = new Set<() => void>();

export const isBootAllowed = (): boolean => bootAllowed;

/** The panel's gate only: closed at install, opened by the first gesture. */
export const setBootAllowed = (allowed: boolean): void => {
  if (bootAllowed === allowed) return;
  bootAllowed = allowed;
  for (const fn of [...bootListeners]) fn();
};

export const subscribeBootAllowed = (fn: () => void): (() => void) => {
  bootListeners.add(fn);
  return () => {
    bootListeners.delete(fn);
  };
};

/** Null when booting is allowed now (always on the web); else resolves when it is. */
export const whenBootAllowed = (): Promise<void> | null => {
  if (bootAllowed) return null;
  return new Promise((resolve) => {
    const off = subscribeBootAllowed(() => {
      if (!bootAllowed) return;
      off();
      resolve();
    });
  });
};

/** For effects that boot a sandbox: re-run when the panel's gate opens. Always true on the web. */
export const useBootAllowed = (): boolean =>
  useSyncExternalStore(subscribeBootAllowed, isBootAllowed, isBootAllowed);

/** Test seam: back to the web's defaults. */
export const __resetHostForTest = (): void => {
  panel = null;
  bootAllowed = true;
  bootListeners.clear();
};
